// @tg-seq: 10037
// @architect-approved: HARDCODED_COLORS - Login overlay matches full login page styling
// @architect-approved: CUSTOM_ORG_FILTER - Login overlay manages authentication flow state from API before Redux hydration

// @tg-golden-rule: BEFORE debugging, validate inside-out (Layer 1-7).
// Read: docs/agent-contracts/AGENT_LEGO_BLOCK_GOLDEN_RULE.md

import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../../app/hooks';
import { setAccessToken, setUserOrganizations } from '../../app/redux/auth/authSlice';
import { navigateToComponent } from '../../utils/directoryNavigation';
import { apiRequest } from '../../utils/apiService';
import { getTsxFileName } from '../../utils/componentIdentity';
import { SecureStorage } from '../../utils/SecureStorage';
// Auth flow steps
type AuthStep = 'login' | 'otp' | 'org-select';

interface Organization {
    organization_uuid: string;
    organization_name: string;
    role_id: number | null;
    is_primary: boolean;
}

interface ReceptionLoginProps {
    onClose?: () => void;
    onLoginSuccess?: () => void;
}

const Self: React.FC<ReceptionLoginProps> = ({ onClose, onLoginSuccess }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // Form state
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Multi-step auth state
    const [authStep, setAuthStep] = useState<AuthStep>('login');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [partialToken, setPartialToken] = useState<string>('');
    const [pfuserUuid, setPfuserUuid] = useState<string>('');
    const [userOrganizations, setUserOrganizationsState] = useState<Organization[]>([]);

    const navigatePostLogin = () => {
        console.log('[Login] navigatePostLogin called, onLoginSuccess exists:', !!onLoginSuccess);

        if (onLoginSuccess) {
            // When called from AuthGuard overlay, close overlay and let AuthGuard handle navigation
            console.log('[Login] Calling onLoginSuccess() to close AuthGuard overlay');
            onLoginSuccess();
        } else {
            // Check for stored post-login redirect
            const redirectPath = SecureStorage.getPostLoginRedirect();
            if (redirectPath) {
                console.log(`[Login] Redirecting to stored destination: ${redirectPath}`);
                localStorage.removeItem('post_login_redirect');
                navigate(redirectPath);
            } else {
                // Default post-login destination: Welcome Directory (seq 10000)
                console.log('[Login] Navigating to Welcome Directory');
                navigate('/welcome');
            }
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/login`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        username: username || undefined,
                        email: username.includes('@') ? username : undefined,
                        password,
                        ttp_id: 0,
                    }),
                },
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Login failed');
            }

            // Check if OTP is required
            if (data.otp_required) {
                setPartialToken(data.access_token);
                SecureStorage.setAccessToken(data.access_token);
                setPfuserUuid(data.pfuser_uuid);
                SecureStorage.setPfuserUuid(data.pfuser_uuid);
                setUserOrganizationsState(data.organizations || []);
                setAuthStep('otp');
                toast.info('Please enter your OTP code');
                return;
            }

            // Check if org selection is required
            if (data.requires_org_selection && data.organizations?.length > 1) {
                setPfuserUuid(data.pfuser_uuid);
                SecureStorage.setPfuserUuid(data.pfuser_uuid);
                setUserOrganizationsState(data.organizations);
                setPartialToken(data.access_token);
                SecureStorage.setAccessToken(data.access_token);
                setAuthStep('org-select');
                toast.info('Select your organization');
                return;
            }

            // Direct login success
            console.log('[Login] Setting tokens in SecureStorage and Redux');
            console.log(
                '[Login] Received access_token:',
                data.access_token?.substring(0, 20) + '...',
            );

            try {
                SecureStorage.setAccessToken(data.access_token);
                console.log('[Login] SecureStorage.setAccessToken() completed');

                SecureStorage.setRefreshToken(data.refresh_token);
                console.log('[Login] SecureStorage.setRefreshToken() completed');

                if (data.pfuser_uuid) {
                    SecureStorage.setPfuserUuid(data.pfuser_uuid);
                    setPfuserUuid(data.pfuser_uuid);
                    console.log('[Login] PFUser UUID set:', data.pfuser_uuid);
                }

                dispatch(setAccessToken(data.access_token));
                console.log('[Login] Redux dispatch(setAccessToken) completed');

                // Verify token was actually stored
                const storedToken = SecureStorage.getAccessToken();
                console.log('[Login] Token verification - exists in localStorage:', !!storedToken);
                console.log(
                    '[Login] Token verification - matches:',
                    storedToken === data.access_token,
                );

                if (!storedToken) {
                    throw new Error('Token was not persisted to localStorage!');
                }

                // Small delay to ensure all state updates complete
                await new Promise((resolve) => setTimeout(resolve, 100));

                // Keyfob pattern: Organization validation happens at navigation, not at login
                // Backend no longer returns organizations[] array - removed for security
                toast.success('Login successful!');
                console.log('[Login] Calling navigatePostLogin()');
                navigatePostLogin();
            } catch (storageError) {
                console.error('[Login] ERROR during token storage:', storageError);
                toast.error('Failed to save login credentials. Please try again.');
                setIsLoading(false);
                return;
            }
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Invalid username or password');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/otp/verify`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        pfuser_uuid: pfuserUuid,
                        otp_code: otpCode,
                    }),
                },
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Invalid OTP code');
            }

            SecureStorage.setAccessToken(data.access_token);
            SecureStorage.setRefreshToken(data.refresh_token);
            if (pfuserUuid) {
                SecureStorage.setPfuserUuid(pfuserUuid);
            }
            dispatch(setAccessToken(data.access_token));

            // Check if org selection needed
            if (data.requires_org_selection && userOrganizations.length > 1) {
                setAuthStep('org-select');
                toast.info('Select your organization');
            } else if (userOrganizations.length === 1) {
                await handleOrganizationSelect(userOrganizations[0].organization_uuid);
            } else {
                toast.success('Login successful!');
                navigatePostLogin();
            }
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Invalid OTP code');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOrganizationSelect = async (organization_uuid: string) => {
        setIsLoading(true);

        try {
            const data = await apiRequest<any>('/api/auth/select-organization', 'POST', {
                pfuser_uuid: pfuserUuid,
                organization_uuid: organizationUuid,
            });

            // Update token with org context
            SecureStorage.setAccessToken(data.access_token);
            localStorage.setItem('active_organization_uuid', organizationUuid);
            dispatch(setAccessToken(data.access_token));

            const selectedOrg = userOrganizations.find(
                (o) => o.organization_uuid === organizationUuid,
            );
            if (selectedOrg) {
                dispatch(
                    setUserOrganizations([
                        {
                            OrganizationId: 0,
                            OrganizationName: selectedOrg.organization_name,
                            Role: data.role_id?.toString(),
                        },
                    ]),
                );
            }

            toast.success(`Welcome to ${data.organization_name}!`);
            navigatePostLogin();
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to select organization');
        } finally {
            setIsLoading(false);
        }
    };

    // Render auth step content
    const renderAuthContent = () => {
        switch (authStep) {
            case 'otp':
                return (
                    <Box component="form" onSubmit={handleOtpVerify} sx={{ width: '100%', mt: 1 }}>
                        <Alert
                            severity="info"
                            sx={{
                                mb: 2,
                                fontSize: '0.8rem',
                                background: 'rgba(20, 102, 239, 0.08)',
                                border: '1px solid rgba(20, 102, 239, 0.2)',
                                color: '#131109',
                            }}
                        >
                            <LockIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                            Enter the 6-digit code from your authenticator app
                        </Alert>
                        <TextField
                            required
                            fullWidth
                            size="small"
                            label="OTP Code"
                            autoFocus
                            value={otpCode}
                            onChange={(e) =>
                                setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                            }
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: '#131109',
                                    fontSize: '1.5rem',
                                    letterSpacing: '0.5rem',
                                    textAlign: 'center',
                                    '& fieldset': { borderColor: '#e7e6df' },
                                    '&:hover fieldset': { borderColor: '#1466ef' },
                                    '&.Mui-focused fieldset': { borderColor: '#1466ef' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(19, 17, 9, 0.6)' },
                            }}
                            slotProps={{
                                htmlInput: { maxLength: 6, pattern: '[0-9]*' },
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="small"
                            disabled={isLoading || otpCode.length !== 6}
                            sx={{
                                mb: 1,
                                py: 1.2,
                                fontSize: '1rem',
                                fontWeight: 600,
                                borderRadius: '6px',
                                background: '#1466ef',
                                color: '#fdfdf9',
                                '&:hover': { background: '#0d5dd9' },
                            }}
                        >
                            {isLoading ? <CircularProgress size={20} /> : 'Verify OTP'}
                        </Button>
                        <Button
                            fullWidth
                            variant="text"
                            size="small"
                            onClick={() => {
                                setAuthStep('login');
                                setOtpCode('');
                            }}
                            sx={{ color: 'rgba(19, 17, 9, 0.6)' }}
                        >
                            Back to Login
                        </Button>
                    </Box>
                );

            case 'org-select':
                return (
                    <Box sx={{ width: '100%', mt: 1 }}>
                        <Alert
                            severity="info"
                            sx={{
                                mb: 2,
                                fontSize: '0.8rem',
                                background: 'rgba(20, 102, 239, 0.08)',
                                border: '1px solid rgba(20, 102, 239, 0.2)',
                                color: '#131109',
                            }}
                        >
                            <BusinessIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                            Select your organization
                        </Alert>
                        <List
                            sx={{
                                bgcolor: 'rgba(131, 121, 105, 0.08)',
                                borderRadius: 2,
                                p: 0,
                                maxHeight: 200,
                                overflow: 'auto',
                            }}
                        >
                            {userOrganizations.map((org, index) => (
                                <React.Fragment key={org.organization_uuid}>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() =>
                                                handleOrganizationSelect(org.organization_uuid)
                                            }
                                            disabled={isLoading}
                                            sx={{
                                                py: 1.5,
                                                '&:hover': {
                                                    background: 'rgba(20, 102, 239, 0.08)',
                                                },
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#131109',
                                                            fontWeight: org.is_primary ? 700 : 400,
                                                        }}
                                                    >
                                                        {org.organization_name}
                                                        {org.is_primary && (
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    ml: 1,
                                                                    color: '#1466ef',
                                                                    fontSize: '0.7rem',
                                                                }}
                                                            >
                                                                (Primary)
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                    {index < userOrganizations.length - 1 && (
                                        <Divider sx={{ borderColor: 'rgba(131, 121, 105, 0.2)' }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </List>
                        <Button
                            fullWidth
                            variant="text"
                            size="small"
                            onClick={() => {
                                setAuthStep('login');
                                setUserOrganizationsState([]);
                            }}
                            sx={{ mt: 1, color: 'rgba(19, 17, 9, 0.6)' }}
                        >
                            Back to Login
                        </Button>
                    </Box>
                );

            default: // login
                return (
                    <Box component="form" onSubmit={handleLogin} sx={{ mt: 1, width: '100%' }}>
                        <TextField
                            required
                            fullWidth
                            size="small"
                            label="Username or Email"
                            autoComplete="username"
                            autoFocus
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            sx={{
                                mb: 1.5,
                                '& .MuiOutlinedInput-root': {
                                    color: '#131109',
                                    '& fieldset': { borderColor: '#e7e6df' },
                                    '&:hover fieldset': { borderColor: '#1466ef' },
                                    '&.Mui-focused fieldset': { borderColor: '#1466ef' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(19, 17, 9, 0.6)' },
                            }}
                        />
                        <TextField
                            required
                            fullWidth
                            size="small"
                            label="Password"
                            type="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: '#131109',
                                    '& fieldset': { borderColor: '#e7e6df' },
                                    '&:hover fieldset': { borderColor: '#1466ef' },
                                    '&.Mui-focused fieldset': { borderColor: '#1466ef' },
                                },
                                '& .MuiInputLabel-root': { color: 'rgba(19, 17, 9, 0.6)' },
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="small"
                            disabled={isLoading}
                            sx={{
                                mb: 1,
                                py: 1.2,
                                fontSize: '1rem',
                                fontWeight: 600,
                                borderRadius: '6px',
                                background: '#1466ef',
                                color: '#fdfdf9',
                                '&:hover': { background: '#0d5dd9' },
                            }}
                        >
                            {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Sign In'}
                        </Button>
                        <Button
                            fullWidth
                            variant="text"
                            size="small"
                            onClick={() => {
                                if (onClose) onClose();
                                navigateToComponent(10023, navigate);
                            }}
                            sx={{ color: 'rgba(19, 17, 9, 0.6)', textTransform: 'none' }}
                        >
                            New here? Sign Up
                        </Button>
                    </Box>
                );
        }
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                // @architect-approved: HARDCODED_COLORS - Standard modal backdrop overlay (semi-transparent black)
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1300,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget && onClose) onClose();
            }}
        >
            <Paper
                elevation={24}
                sx={{
                    width: 360,
                    maxWidth: '90vw',
                    p: 3,
                    borderRadius: '8px',
                    background: '#fdfdf9', // Card surface - matches full login
                    border: '1px solid rgba(131, 121, 105, 0.2)',
                    boxShadow: '0 8px 32px rgba(27, 26, 23, 0.12)',
                    position: 'relative',
                }}
            >
                {/* Close button */}
                {onClose && (
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}

                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#131109', mb: 0.5 }}>
                        {authStep === 'otp'
                            ? '🔒 Two-Factor Auth'
                            : authStep === 'org-select'
                              ? '🏢 Select Organization'
                              : '🔐 Quick Login'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(19, 17, 9, 0.6)' }}>
                        {authStep === 'login' && 'Sign in to access the Collaboration Center'}
                    </Typography>
                </Box>

                {renderAuthContent()}
            </Paper>
        </Box>
    );
};

Self.displayName = getTsxFileName(import.meta.url);
export default Self;
